import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Pencil, Trash2, MessageSquareText, Loader2, RotateCcw } from 'lucide-react'
import { api } from '@/lib/api'
import { pedirExclusao } from '@/lib/confirmarExclusao'
import toast from 'react-hot-toast'

interface Msg {
  id?: number; texto: string; destino: string; condicao: string
  condicao_valor: string | null; ordem: number; ativo: boolean; filial_id?: number | null
}
const EMPTY: Msg = { texto: '', destino: 'infCpl', condicao: 'sempre', condicao_valor: '', ordem: 0, ativo: true }

const CONDICOES: { v: string; label: string }[] = [
  { v: 'sempre', label: 'Sempre (toda nota)' },
  { v: 'tem_st', label: 'Quando a nota tem ICMS-ST' },
  { v: 'sem_st', label: 'Quando a nota NÃO tem ST' },
  { v: 'interestadual', label: 'Venda interestadual (fora do estado)' },
  { v: 'interna', label: 'Venda dentro do estado' },
  { v: 'tem_difal', label: 'Quando tem DIFAL' },
  { v: 'regime_normal', label: 'Regime Normal' },
  { v: 'regime_simples', label: 'Simples Nacional' },
  { v: 'perfil_loja', label: 'Só nas filiais Loja' },
  { v: 'perfil_industria', label: 'Só nas filiais Indústria' },
  { v: 'bonificacao', label: 'Bonificação' },
  { v: 'comodato', label: 'Comodato' },
  { v: 'cfop', label: 'CFOP específico (lista)' },
  { v: 'uf_dest', label: 'UF de destino (lista)' },
]
const condLabel = (v: string) => CONDICOES.find(c => c.v === v)?.label || v

export default function MensagensFiscaisPage() {
  const qc = useQueryClient()
  const [form, setForm] = useState<Msg | null>(null)

  const { data: msgs = [], isLoading } = useQuery<Msg[]>({
    queryKey: ['mensagens-fiscais'],
    queryFn: async () => (await api.get('/fiscal/mensagens-fiscais')).data,
  })

  const salvar = useMutation({
    mutationFn: async (m: Msg) => m.id
      ? (await api.put('/fiscal/mensagens-fiscais/' + m.id, m)).data
      : (await api.post('/fiscal/mensagens-fiscais', m)).data,
    onSuccess: () => { toast.success('Mensagem salva'); setForm(null); qc.invalidateQueries({ queryKey: ['mensagens-fiscais'] }) },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao salvar'),
  })
  const excluir = useMutation({
    mutationFn: async (id: number) => (await api.delete('/fiscal/mensagens-fiscais/' + id)).data,
    onSuccess: () => { toast.success('Mensagem excluída'); qc.invalidateQueries({ queryKey: ['mensagens-fiscais'] }) },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao excluir'),
  })
  const restaurar = useMutation({
    mutationFn: async () => (await api.post('/fiscal/mensagens-fiscais/seed-winthor?substituir=true')).data,
    onSuccess: (d: any) => { toast.success(`${d.criadas} regras padrão restauradas`); qc.invalidateQueries({ queryKey: ['mensagens-fiscais'] }) },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao restaurar'),
  })

  return (
    <div className="p-4 lg:p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl font-bold text-ink-900 flex items-center gap-2"><MessageSquareText size={24} /> Mensagens da Nota (Inf. Complementares)</h1>
        <div className="flex gap-2">
          <button className="btn-secondary" onClick={() => confirm('Restaurar as regras padrão do WinThor? Isso substitui as atuais.') && restaurar.mutate()}>
            <RotateCcw size={14} /> Padrão WinThor
          </button>
          <button className="btn-primary" onClick={() => setForm({ ...EMPTY, ordem: (msgs.at(-1)?.ordem ?? 0) + 1 })}><Plus size={14} /> Nova mensagem</button>
        </div>
      </div>

      <div className="card card-body bg-sky-50 border-sky-200 text-xs text-sky-800">
        As mensagens entram no campo <strong>Informações Complementares</strong> da NF-e conforme a <strong>condição</strong> de cada regra
        (igual ao WinThor — o destaque de ICMS-ST só sai quando a nota tem ST). Placeholders: <code>{'{pedido}'}</code>, <code>{'{total}'}</code>,
        <code>{' {valor_st}'}</code>, <code>{' {tributos_aprox}'}</code> (Lei da Transparência — IBPT).
      </div>

      <div className="table-wrap">
        <table className="table">
          <thead><tr>
            <th className="w-12">Ordem</th>
            <th className="w-56">Quando aparece</th>
            <th>Texto</th>
            <th className="w-16 text-center">Ativo</th>
            <th className="w-20"></th>
          </tr></thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={5} className="text-center py-8 text-ink-400"><Loader2 className="animate-spin inline" size={16} /> Carregando...</td></tr>
            ) : msgs.length === 0 ? (
              <tr><td colSpan={5} className="text-center py-8 text-ink-400">Nenhuma mensagem — clique em "Padrão WinThor" pra começar</td></tr>
            ) : msgs.map(m => (
              <tr key={m.id} className="hover:bg-ink-50 group align-top">
                <td className="font-mono text-center">{m.ordem}</td>
                <td className="whitespace-nowrap text-sm">
                  <span className="px-2 py-0.5 rounded bg-ink-100 text-ink-700 text-xs">{condLabel(m.condicao)}</span>
                  {m.condicao_valor && <span className="ml-1 font-mono text-xs text-ink-500">{m.condicao_valor}</span>}
                </td>
                {/* w-full + break-words: sem largura declarada o <td> estica pra caber
                    o texto inteiro numa linha e joga as colunas seguintes pra fora da
                    tela. max-w-* sozinho não resolve em <table>. */}
                <td className="text-sm text-ink-700 w-full break-words">
                  <div className="max-w-[60ch] line-clamp-3" title={m.texto}>{m.texto}</div>
                </td>
                <td className="text-center">{m.ativo ? '✓' : '—'}</td>
                <td className="text-right whitespace-nowrap">
                  <button className="opacity-0 group-hover:opacity-100 text-ink-500 hover:text-brand-600 px-1" onClick={() => setForm({ ...m })}><Pencil size={14} /></button>
                  <button className="opacity-0 group-hover:opacity-100 text-ink-500 hover:text-red-600 px-1" onClick={() => m.id && pedirExclusao({ descricao: 'esta mensagem', onConfirm: () => excluir.mutate(m.id!) })}><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {form && (
        <div className="fixed inset-0 bg-ink-950/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
            <div className="px-5 py-4 border-b"><h3 className="font-semibold text-lg">{form.id ? 'Editar' : 'Nova'} mensagem da nota</h3></div>
            <div className="px-5 py-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="label">Quando aparece</label>
                  <select className="input" value={form.condicao} onChange={e => setForm({ ...form, condicao: e.target.value })}>
                    {CONDICOES.map(c => <option key={c.v} value={c.v}>{c.label}</option>)}
                  </select></div>
                <div><label className="label">Ordem</label>
                  <input type="number" className="input" value={form.ordem} onChange={e => setForm({ ...form, ordem: Number(e.target.value) })} /></div>
              </div>
              {(form.condicao === 'cfop' || form.condicao === 'uf_dest') && (
                <div><label className="label">{form.condicao === 'cfop' ? 'CFOPs (separados por vírgula)' : 'UFs de destino (vírgula)'}</label>
                  <input className="input font-mono" value={form.condicao_valor || ''} onChange={e => setForm({ ...form, condicao_valor: e.target.value })}
                    placeholder={form.condicao === 'cfop' ? '5102, 6102' : 'PB, PE, PI'} /></div>
              )}
              <div><label className="label">Texto da mensagem</label>
                <textarea className="input min-h-[90px]" value={form.texto} onChange={e => setForm({ ...form, texto: e.target.value })}
                  placeholder="Use {pedido}, {total}, {valor_st}, {tributos_aprox}" /></div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.ativo} onChange={e => setForm({ ...form, ativo: e.target.checked })} /> Ativo
              </label>
            </div>
            <div className="px-5 py-4 border-t flex gap-2">
              <button onClick={() => setForm(null)} className="btn-secondary flex-1">Cancelar</button>
              <button onClick={() => salvar.mutate(form)} disabled={!form.texto || salvar.isPending} className="btn-primary flex-1">
                {form.id ? 'Salvar' : 'Criar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
